'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useImageCompression } from '@/entities/image-upload';
import { generateId } from '@/shared/lib/common';
import { MEGA_BYTES } from '@/shared/lib/file';
import { getImageProxyUrl } from '@/shared/lib/proxy-url';
import { useThrottleCallback } from '@/shared/lib/useThrottleCallback';
import { RevealOnScroll } from '@/shared/ui/reveal-on-scroll';
import { useToast } from '@ncai-axe/react-ui-v3';
import IcAlert from '@ncai-axe/react-ui-v3/assets/svg/icons/ic_alert_circle_fill.svg';
import { useTranslations } from 'next-intl';
import styles from './SecAiExperience.module.scss';
import { isVtonUploadOnCooldown, loadVtonCache, saveVtonCache, saveVtonUploadCooldown } from '../../lib/vton-cache';
import { VtonClientError, pollResult, runJob } from '../../lib/vton-client';
import { GenerateButton } from '../components/ai-experience/GenerateButton';
import { LoadingView } from '../components/ai-experience/LoadingView';
import { RestrictionModal } from '../components/ai-experience/RestrictionModal';
import { ResultView } from '../components/ai-experience/ResultView';
import { SampleImagePicker } from '../components/ai-experience/SampleImagePicker';
import { UploadDropzone, type UploadRejectReason } from '../components/ai-experience/UploadDropzone';
import { AI_EXPERIENCE_SAMPLES, type AiExperienceSampleId, getAiExperienceSample } from '../components/ai-experience/samples';
type State = {
  kind: 'idle';
} | {
  kind: 'fileSelected';
  file: File;
} | {
  kind: 'sampleSelected';
  sampleId: AiExperienceSampleId;
} | {
  kind: 'uploading';
} | {
  kind: 'running';
} | {
  kind: 'polling';
  requestId: string;
} | {
  kind: 'completed';
  downloadUrl: string;
  isFallback: boolean;
} | {
  kind: 'failed';
  error: string;
};
const SAMPLE_MARQUEE_IMAGES = Array.from({
  length: 20
}, (_, index) => ({
  id: `sample-${index + 1}`,
  image: `/commerce/static/intro/vton/vton_sample_${index + 1}.webp`
})) as ReadonlyArray<{
  id: string;
  image: string;
}>;
const LOADING_MESSAGE_INDEXES = [0, 1, 2, 3, 4, 5] as const;
const LOADING_MESSAGE_COUNT = LOADING_MESSAGE_INDEXES.length;
const FALLBACK_DOWNLOAD_URL = '/intro/sample_output_1.jpg';
const UPLOAD_MAX_FILE_SIZE_BYTES = 4 * MEGA_BYTES;
const UPLOAD_ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/heic'] as const;
const isBusyState = (state: State): boolean => state.kind === 'uploading' || state.kind === 'running' || state.kind === 'polling';
const isGeneratingState = (state: State): boolean => state.kind === 'running' || state.kind === 'polling';
type GenerationErrorKey = 'http4xx' | 'http5xx' | 'generic';
const getGenerationErrorKey = (error: unknown): GenerationErrorKey => {
  if (error instanceof VtonClientError) {
    if (error.code === 'HTTP_4XX') {
      return 'http4xx';
    }
    if (error.code === 'HTTP_5XX') {
      return 'http5xx';
    }
  }
  return 'generic';
};
const loadSampleBlob = async (sampleUrl: string, signal: AbortSignal): Promise<Blob> => new Promise<Blob>((resolve, reject) => {
  const request = new XMLHttpRequest();
  const handleAbort = () => {
    request.abort();
    reject(new VtonClientError('Sample loading was aborted', 'NETWORK'));
  };
  if (signal.aborted) {
    handleAbort();
    return;
  }
  request.open('GET', sampleUrl);
  request.responseType = 'blob';
  request.onload = () => {
    signal.removeEventListener('abort', handleAbort);
    if (request.status >= 200 && request.status < 300 && request.response instanceof Blob) {
      resolve(request.response);
      return;
    }
    reject(new VtonClientError('Sample loading failed', 'NETWORK'));
  };
  request.onerror = () => {
    signal.removeEventListener('abort', handleAbort);
    reject(new VtonClientError('Sample loading failed', 'NETWORK'));
  };
  signal.addEventListener('abort', handleAbort, {
    once: true
  });
  request.send();
});

// eslint-disable-next-line no-unused-vars -- 샘플 File 생성 플로우 보존 (현재 미사용)
const createSampleFile = async (sampleId: AiExperienceSampleId, signal: AbortSignal): Promise<File> => {
  const sample = getAiExperienceSample(sampleId);
  const fetchUrl = getImageProxyUrl({
    src: sample.src
  });
  const blob = await loadSampleBlob(fetchUrl, signal);
  const fileName = sample.src.split('/').pop() ?? `${sample.id}.bin`;
  return new File([blob], fileName, {
    type: blob.type || 'image/webp'
  });
};
export const SecAiExperience = () => {
  const t = useTranslations('intro');
  const [state, setState] = useState<State>({
    kind: 'idle'
  });
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [selectedSampleId, setSelectedSampleId] = useState<AiExperienceSampleId | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const previousPreviewRef = useRef<string | null>(null);
  const cachedObjectUrlRef = useRef<string | null>(null);
  const {
    toast
  } = useToast();
  const {
    compressImage
  } = useImageCompression({
    compressionMode: 'reencode-when-resize-only',
    profile: 'public'
  });
  const notifyError = useCallback((message: string) => {
    toast({
      text: message,
      status: 'negative',
      icon: <IcAlert />,
      duration: 3000
    });
  }, [toast]);
  const safeLoadingIndex = LOADING_MESSAGE_INDEXES[loadingMessageIndex] ?? LOADING_MESSAGE_INDEXES[0];
  const currentLoadingMessage = t(`aiExperience.loadingMessages.${safeLoadingIndex}`);
  const isBusy = isBusyState(state);
  const isGenerating = isGeneratingState(state);
  const isUploading = state.kind === 'uploading';
  const completedImageSrc = useMemo(() => {
    if (state.kind !== 'completed') {
      return FALLBACK_DOWNLOAD_URL;
    }
    return state.downloadUrl;
  }, [state]);
  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return () => {
      controller.abort();
      abortControllerRef.current = null;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    loadVtonCache().then(entry => {
      if (cancelled || !entry) return;
      cachedObjectUrlRef.current = entry.imageObjectUrl;
      setState({
        kind: 'completed',
        downloadUrl: entry.imageObjectUrl,
        isFallback: entry.isFallback
      });
    });
    return () => {
      cancelled = true;
      if (cachedObjectUrlRef.current) {
        URL.revokeObjectURL(cachedObjectUrlRef.current);
        cachedObjectUrlRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[SecAiExperience] state transition', state.kind);
    }
  }, [state.kind]);
  useEffect(() => {
    if (!isBusy) {
      setLoadingMessageIndex(0);
      return;
    }
    const intervalId = window.setInterval(() => {
      setLoadingMessageIndex(previous => (previous + 1) % LOADING_MESSAGE_COUNT);
    }, 2000);
    return () => window.clearInterval(intervalId);
  }, [isBusy]);
  useEffect(() => {
    return () => {
      if (previousPreviewRef.current) {
        URL.revokeObjectURL(previousPreviewRef.current);
      }
    };
  }, []);
  const resetAbortController = useCallback(() => {
    abortControllerRef.current?.abort();
    const controller_0 = new AbortController();
    abortControllerRef.current = controller_0;
    return controller_0;
  }, []);
  const clearPreviewObjectUrl = useCallback(() => {
    if (previousPreviewRef.current) {
      URL.revokeObjectURL(previousPreviewRef.current);
      previousPreviewRef.current = null;
    }
  }, []);
  const handleFile = useCallback((file: File) => {
    clearPreviewObjectUrl();
    const nextPreviewSrc = URL.createObjectURL(file);
    previousPreviewRef.current = nextPreviewSrc;
    setPreviewSrc(nextPreviewSrc);
    setSelectedSampleId(null);
    setState({
      kind: 'fileSelected',
      file
    });
  }, [clearPreviewObjectUrl]);
  const handleReject = useCallback((reason: UploadRejectReason) => {
    const message_0 = reason === 'type' ? t('aiExperience.reject.type') : t('aiExperience.reject.size');
    notifyError(message_0);
  }, [notifyError, t]);
  const handleSampleSelect = useThrottleCallback((...args) => {
    const [sampleId] = args as [AiExperienceSampleId];
    clearPreviewObjectUrl();
    setPreviewSrc(getAiExperienceSample(sampleId).src);
    setSelectedSampleId(sampleId);
    setState({
      kind: 'sampleSelected',
      sampleId
    });
  }) as (sampleId: AiExperienceSampleId) => void;
  const handleReset = useCallback(() => {
    resetAbortController();
    clearPreviewObjectUrl();
    setPreviewSrc(null);
    setSelectedSampleId(null);
    setLoadingMessageIndex(0);
    setState({
      kind: 'idle'
    });
  }, [clearPreviewObjectUrl, resetAbortController]);
  const handleGenerate = useThrottleCallback(async () => {
    if (state.kind !== 'fileSelected' && state.kind !== 'sampleSelected') {
      return;
    }
    if (state.kind === 'sampleSelected') {
      const sample = getAiExperienceSample(state.sampleId);
      setState({
        kind: 'completed',
        downloadUrl: sample.presetResultSrc,
        isFallback: false
      });
      return;
    }
    if (isVtonUploadOnCooldown()) {
      setState({
        kind: 'failed',
        error: t('aiExperience.error.cooldown')
      });
      return;
    }
    const controller_1 = resetAbortController();
    try {
      setState({
        kind: 'uploading'
      });
      let fileToUpload: File = state.file;
      try {
        const compressed = await compressImage(generateId(), state.file, {
          minImageWidth: 350,
          minImageHeight: 350
        });
        const extension = compressed.contentType.split('/')[1] ?? 'webp';
        fileToUpload = new File([compressed.compressedBlob], state.file.name.replace(/\.[^.]+$/, `.${extension}`), {
          type: compressed.contentType
        });
      } catch (compressionError) {
        notifyError(compressionError instanceof Error ? compressionError.message : t('aiExperience.error.processFailed'));
        setState({
          kind: 'fileSelected',
          file: state.file
        });
        return;
      }
      if (controller_1.signal.aborted) {
        return;
      }
      setState({
        kind: 'running'
      });
      const job = await runJob(fileToUpload);
      if (controller_1.signal.aborted) {
        return;
      }
      setState({
        kind: 'polling',
        requestId: job.requestId
      });
      const result = await pollResult(job.jobId, job.requestId, controller_1.signal);
      if (controller_1.signal.aborted) {
        return;
      }
      if (result.kind === 'completed') {
        const dataUrl = `data:${result.imageContentType};base64,${result.image}`;
        setState({
          kind: 'completed',
          downloadUrl: dataUrl,
          isFallback: false
        });
        saveVtonUploadCooldown();
        saveVtonCache(dataUrl, false);
        return;
      }
      if (result.kind === 'timeout') {
        setState({
          kind: 'completed',
          downloadUrl: FALLBACK_DOWNLOAD_URL,
          isFallback: true
        });
        saveVtonUploadCooldown();
        saveVtonCache(FALLBACK_DOWNLOAD_URL, true);
        return;
      }
      notifyError(result.error);
      setState({
        kind: 'fileSelected',
        file: state.file
      });
    } catch (error: unknown) {
      if (controller_1.signal.aborted) {
        return;
      }
      notifyError(t(`aiExperience.error.${getGenerationErrorKey(error)}`));
      setState({
        kind: 'fileSelected',
        file: state.file
      });
    }
  });
  const handleModalClose = useCallback(() => {
    setState({
      kind: 'idle'
    });
  }, []);
  const handleModalPrimaryClick = useCallback(() => {
    handleSampleSelect(AI_EXPERIENCE_SAMPLES[0].id);
  }, [handleSampleSelect]);
  return <section id="ai-experience" data-section="ai-experience" className={styles.section}>
      <div className={styles.background} aria-hidden="true">
        <div className={styles.backgroundGradient} />
        <div className={styles.backgroundGlow} />
      </div>

      <div className={styles.container}>
        <RevealOnScroll delayMs={100} y={16}>
          <div className={styles.headingBlock}>
            <h2 className={styles.heading}>{t('aiExperience.heading')}</h2>
            <p className={styles.description}>
              {t('aiExperience.description')}
            </p>
          </div>
        </RevealOnScroll>

        <div className={styles.experienceWrap}>
          {isGenerating ? <div className={styles.loadingPanel}>
              <LoadingView message={currentLoadingMessage} />

              <div className={styles.loadingCaptionWrap}>
                <p className={styles.loadingCaption}>
                  {t('aiExperience.loadingCaption')}
                </p>
              </div>

              <div className={styles.marqueeViewport}>
                <div className={styles.marqueeTrack}>
                  {[...SAMPLE_MARQUEE_IMAGES, ...SAMPLE_MARQUEE_IMAGES].map((item, index) => <div key={`${item.id}-${index}`} className={styles.marqueeItem}>
                        <img src={getImageProxyUrl({
                  src: item.image
                })} alt="" className={styles.marqueeImage} />
                      </div>)}
                </div>
              </div>
            </div> : state.kind === 'completed' ? <div className={styles.resultPanel}>
              <ResultView imageSrc={completedImageSrc} isFallback={state.isFallback} onRetry={handleReset} />
              <div className={styles.authCtaWrap} />
            </div> : <div className={styles.inputPanel}>
              <div className={styles.uploadWrap}>
                <UploadDropzone onFile={handleFile} onReject={handleReject} previewSrc={previewSrc} disabled={isBusy} isUploading={isUploading} maxFileSizeBytes={UPLOAD_MAX_FILE_SIZE_BYTES} acceptedMimeTypes={UPLOAD_ACCEPTED_MIME_TYPES} minImageWidth={350} />
              </div>

              <div className={styles.sampleWrap}>
                <SampleImagePicker onSelect={handleSampleSelect} selectedId={selectedSampleId} disabled={isBusy} />
              </div>

              <div className={styles.generateWrap}>
                <GenerateButton onClick={handleGenerate} disabled={state.kind !== 'fileSelected' && state.kind !== 'sampleSelected'} loading={isBusy} />
              </div>
            </div>}
        </div>
      </div>

      <RestrictionModal open={state.kind === 'failed'} onClose={handleModalClose} onPrimaryClick={handleModalPrimaryClick} />
    </section>;
};